import React from 'react';
import { ButtonBase, ButtonBaseProps } from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';

export interface AbstractButtonProps
  extends Omit<ButtonBaseProps, keyof PounceStyleProps>, PounceStyleProps {
  /** The HTML element or React component to render as the button */
  as?: React.ElementType;

  /** Additional styles to apply (combined with pounce props) */
  sx?: ButtonBaseProps['sx'];
}

/**
 * PounceV2 AbstractButton component built on top of MUI ButtonBase.
 *
 * The AbstractButton is a general-purpose button with no styles or theme attached to it.
 * It inherits the same CSS properties that Box has and should be used whenever you want
 * to build a custom element that should be clickable.
 *
 * This button can also wrap other existing Pounce elements as a way of making them
 * clickable without messing with their UI.
 *
 * Features:
 * - Full pounce API compatibility (spacing, colors, typography, layout, etc.)
 * - Automatic theme value resolution
 * - MUI accessibility and interaction patterns
 * - Polymorphic component support with 'as' prop
 */
export const AbstractButton = React.forwardRef<HTMLButtonElement, AbstractButtonProps>(
  function AbstractButton({ as = 'button', children, sx, type = 'button', ...rest }, ref) {
    const { pounceProps, otherProps } = extractPounceProps(rest);
    const pounceStyles = mapPouncePropsToSx(pounceProps);

    // Combine default styles, pounce-generated styles, and user-provided sx
    const combinedSx: ButtonBaseProps['sx'] = {
      ...pounceStyles,
      ...(sx || {}),
    } as ButtonBaseProps['sx'];

    return (
      <ButtonBase
        ref={ref}
        component={as}
        type={type}
        disableRipple
        sx={combinedSx}
        {...otherProps}
      >
        {children}
      </ButtonBase>
    );
  },
);
